/* eslint-disable @next/next/no-img-element */
import { IPlatform, IPlugin, THit } from '@/types';
import Image from 'next/image';
import { Rating } from 'react-simple-star-rating';
import sanitizeHtml from 'sanitize-html';
import defaultImage from '../../../public/assets/image-placeholder.png';
import { Breadcrumbs } from '../breadcrumbs/breadcrumbs.comp';
import { CustomLink } from '../link/link.comp';
import { StyledHero } from './appInfo.styles';

export const AppInfo = ({
	appSlug,
	app,
	promoApp,
	platform,
}: {
	appSlug: string;
	app: THit;
	promoApp: IPlugin;
	platform: IPlatform;
}) => {
	const breadcrumbs = [
		{ name: 'Discover', url: '/' },
		{
			name: `${platform.displayName}`,
			url: `/${platform?.name || ''}`,
		},
		{
			name: `${app.name}`,
			url: `/${platform?.name || ''}/${platform.appSynonym}/${appSlug}`,
		},
	];

	let installUrl = app.referralLink || app.platformAppLink;

	if (installUrl.includes('?')) {
		installUrl += '&';
	} else {
		installUrl += '?';
	}

	installUrl += 'ref=commonninja';

	function removeHTMLTags(html: string) {
		const clean = sanitizeHtml(html, {
			allowedTags: [
				'address',
				'article',
				'aside',
				'footer',
				'header',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'hgroup',
				'nav',
				'section',
				'blockquote',
				'dd',
				'div',
				'dl',
				'dt',
				'figcaption',
				'figure',
				'hr',
				'li',
				'ol',
				'p',
				'pre',
				'ul',
				'a',
				'b',
				'br',
				'em',
				'i',
				'mark',
				'q',
				'rb',
				'rp',
				'rt',
				'rtc',
				'ruby',
				's',
				'samp',
				'small',
				'span',
				'strong',
				'time',
				'u',
				'caption',
				'col',
				'colgroup',
				'table',
				'tbody',
				'td',
				'tfoot',
				'th',
				'thead',
				'tr',
			],
		});

		return clean;
	}

	return (
		<StyledHero>
			<div className="hero-content-container">
				<Breadcrumbs breadcrumbs={breadcrumbs} />
				<div className="app-header">
					<div className="left-side">
						<Image
							width={100}
							height={100}
							src={app.icon || defaultImage}
							alt={(app?.name || '').replace(/\&#x2011;/g, '- ')}
						/>
						<div className="details">
							<h1>{(app?.name || '').replace(/\&#x2011;/g, '- ')}</h1>
							<h2>
								{platform.displayName} {platform.appSynonym}
							</h2>
						</div>
					</div>
					<CustomLink
						rel="nofollow"
						target={'_blank'}
						className="install-btn"
						href={installUrl}
						variant="secondary"
					>
						Install on {platform.displayName}
					</CustomLink>
				</div>
				<div className="app-info">
					<h3>App Details</h3>
					{app.description && (
						<div
							className="info-text"
							dangerouslySetInnerHTML={{
								__html: removeHTMLTags(app.description),
							}}
						></div>
					)}
					{!app.description && <p className="info-text">{app.teaser}</p>}
					<h3>Pricing</h3>
					<p className="info-text">
						Starting from <em>${app.pricing?.lowestPrice} </em>
						{app.pricing?.highestPrice > 0 &&
							app.pricing?.highestPrice !== app.pricing?.lowestPrice && (
								<em>to ${app.pricing?.highestPrice} </em>
							)}
						per month.
					</p>
				</div>
			</div>
			<div className="cards">
				<div className="card promo">
					<h3>Check Out the {promoApp?.name || ''} Widget</h3>
					<p>By Common Ninja</p>
					<CustomLink
						variant="secondary"
						className="img-link"
						href={`https://www.commoninja.com/widgets/${
							promoApp?.slug || ''
						}?ref=searchengine`}
					>
						<Image
							width={275}
							height={275}
							src={promoApp?.promotionImage || ''}
							alt={promoApp?.name || ''}
						/>
					</CustomLink>
					<CustomLink
						className="cta"
						href={`https://www.commoninja.com/widgets/${
							promoApp?.slug || ''
						}?via=discover&ref=searchengine&utm_source=discover&utm_medium=referral&utm_campaign=${
							promoApp?.name || ''
						}`}
					>
						Try For Free!
					</CustomLink>
				</div>
				<div className="card info">
					<h3>App Info</h3>
					<div className="stat">
						<p className="title">Rating</p>
						<Rating
							size={22}
							readonly={true}
							fillColor={'#FFC657'}
							emptyColor={'#4D5265'}
							allowFraction
							initialValue={Math.round(app.rating)}
						/>
					</div>
					<div className="stat">
						<p className="title">Reviewers</p>
						<p>
							{' '}
							{app.numberOfReviews
								? app.numberOfReviews.toLocaleString()
								: 'No'}{' '}
							reviews
						</p>
					</div>
					<div className="stat">
						<p className="title">Tags</p>
						<div className="tags">
							{app.tags.length > 0 ? (
								<div className="tags">
									{app.tags.map((t) => (
										<div key={t} className="tag">
											{t}
										</div>
									))}
								</div>
							) : (
								<p>No tags</p>
							)}
						</div>
					</div>
					{app.languages.length > 0 && (
						<div className="stat">
							<p className="title">Languages</p>
							{
								<div className="tags">
									{app.languages.map((t) => (
										<div className="tag" key={t}>
											{t}
										</div>
									))}
								</div>
							}
						</div>
					)}
					<div className="stat">
						<p className="title">Developed By</p>
						<p>{app?.developer?.name || 'Unknown'}</p>
					</div>
				</div>
			</div>
		</StyledHero>
	);
};
